import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import ImageUploadButton from "../components/Buttons/ImageUploadButton";
import SimpleButton from "../components/Buttons/SimpleButton";
import Checkbox from "../components/Checkbox/Checkbox";
import RoundedProfilePic from "../components/Images/RoundedProfilePic";
import ErrorInputWithIcon from "../components/Inputs/ErrorInputWithIcon";
import InputWithLabel from "../components/InputWithLabel/InputWithLabel";
import LogoImg from "../components/Logo/LogoImg";
import Row from "../components/Row/Row";
import ColoredTitle from "../components/Text/ColoredTitle";
import ErrorText from "../components/Text/ErrorText";
import InputErrorText from "../components/Text/InputErrorText";
import RequiredText from "../components/Text/RequiredText";
import Text from "../components/Text/Text";
import { useAuth } from "../hooks/useAuth";
import default_user_pic from "../imgs/default_user_pic.svg";
import magic_star from "../imgs/magic_star.svg";
import upload_icon from "../imgs/upload.svg";
import upload_icon_light from "../imgs/upload_icon_green.svg";
import user_svg from "../imgs/user.svg";

const NewArtistInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #000d30;
  background-size: cover;
  min-height: 100vh;
  padding-top: 5%;

  @media screen and (max-width: 500px) {
    padding: 32px;
  }
`;

const ComposeLogo = styled.div`
  position: absolute;
  align-self: start;
  left: 0;
  top: 0;
`;

const ArtistInfo = styled.div`
  display: flex;
  flex-direction: column;
  border: ${(props) =>
    props.theme.themeType === "dark" ? "1px solid white" : "2px solid #9D7CFF"};
  width: 35%;
  align-items: center;
  border-radius: 20px;
  background: ${(props) =>
    props.theme.themeType === "dark" ? "#000D30" : "white"};
  padding-inline: 72px;
  padding-top: 32px;
  padding-bottom: 32px;
  @media screen and (max-width: 500px) {
    width: 100%;
    padding-inline: 16px;
    margin-top: 32px;
  }
`;

const SavedStateContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const ArtistInfoForm = styled.div`
  align-self: start;
  width: 94%;
  margin-bottom: 32px;

  @media screen and (max-width: 500px) {
    width: 90%;
  }
`;

const ProfilePic = styled.div`
  display: flex;
`;

const OKButton = styled(SimpleButton)`
  align-self: center;
  margin-top: 60px;
`;

const ThankYouText = styled(ColoredTitle)`
  background: #fff;
  text-align: center;
  align-self: center;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const WelcomeArtistText = styled(ColoredTitle)`
  background: #fff;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  text-align: center;
  margin: 0;
`;
const RowWithPadding = styled(Row)`
  padding-top: 50px;
`;

interface NewArtistInfoProps {
  theme: string;
}

export const NewArtistInfoPage: React.FC<NewArtistInfoProps> = ({ theme }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [stageName, setStageName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [stageNameValid, setStageNameValid] = useState(true);
  const [firstNameValid, setFirstNameValid] = useState(true);
  const [lastNameValid, setLastNameValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState<File>();
  const [imageUrl, setImageUrl] = useState("");
  const [useFullNameAsStageName, setUseFullNameAsStageName] = useState(false);

  const auth = useAuth();

  useEffect(() => {
    if (auth && auth.user) {
      setImageUrl(
        auth.user.profile_pic_s3_link === null
          ? default_user_pic
          : auth.user.profile_pic_s3_link
      );
    }
    if (selectedImage) {
      setImageUrl(URL.createObjectURL(selectedImage));
    }
  }, [selectedImage]);

  const handleStageNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStageName(event.target.value);
    setStageNameValid(event.target.value !== "");
  };

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFirstName(event.target.value);
    setFirstNameValid(event.target.value !== "");
  };
  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
    setLastNameValid(event.target.value !== "");
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setEmailValid(
      event.target.value !== "" && event.target.value.includes("@")
    );
  };

  const validateFields = () => {
    setFirstNameValid(firstName.trim().length > 0);
    setStageNameValid(stageName.trim().length > 0);
    setLastNameValid(lastName.trim().length > 0);
    setEmailValid(email.trim().length > 0);
    if (selectedImage === undefined) {
      setErrorMessage("Please upload a profile picture before continuing");
      setError(true);
    }
    if (useFullNameAsStageName) {
      return (
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        selectedImage !== undefined
      );
    }
    return (
      firstName.trim().length > 0 &&
      stageName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      selectedImage !== undefined
    );
  };

  const handleSubmit = () => {
    if (!validateFields()) {
      return;
    }

    const formData = new FormData();
    if (selectedImage) {
      formData.append("profile_pic", selectedImage);
    }
    formData.append("artist_first_name", firstName.trim());
    if (useFullNameAsStageName) {
      formData.append("stage_name", firstName.trim() + " " + lastName.trim());
    } else {
      formData.append("stage_name", stageName.trim());
    }
    formData.append("artist_last_name", lastName.trim());
    formData.append("email", email.trim());
    if (auth && auth.user) {
      formData.append("wallet_address", auth.user.wallet_address);
      axios
        .post(process.env.REACT_APP_BACKEND_URL + "/update_user", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setSaved(true);
        })
        .catch(() => {
          setError(true);
          setErrorMessage("Something went wrong, please try again");
        });
    }
  };

  const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedImage(event.target.files[0]);
      if (
        errorMessage === "Please upload a profile picture before continuing"
      ) {
        setErrorMessage("");
        setError(false);
      }
    }
  };

  const onOkClick = () => {
    navigate("/user/" + auth.user.wallet_address);
  };

  const onCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseFullNameAsStageName(event.target.checked);
  };

  return (
    <>
      <ComposeLogo>
        <Link to="/">
          <LogoImg />
        </Link>
      </ComposeLogo>
      <NewArtistInfoContainer>
        {!saved && (
          <ArtistInfo>
            <WelcomeArtistText>Welcome New Artist!</WelcomeArtistText>
            <p>Please complete your profile to continue creating NFTs</p>

            <ProfilePic>
              <RoundedProfilePic
                bg={imageUrl}
                error={error && selectedImage === undefined}
                outline={false}
              />
              <ImageUploadButton
                align={"end"}
                bg={theme === "dark" ? upload_icon : upload_icon_light}
                onImageChange={onImageChange}
                htmlFor="uploadPic"
              />
            </ProfilePic>

            {!(error && selectedImage === undefined) && (
              <Text>Upload a profile picture</Text>
            )}
            {error && selectedImage === undefined && (
              <ErrorText>Upload a profile picture</ErrorText>
            )}
            {error && <ErrorText>{errorMessage}</ErrorText>}

            <ArtistInfoForm>
              <InputWithLabel>
                <RequiredText>First Name</RequiredText>
                <ErrorInputWithIcon
                  valid={firstNameValid}
                  src={user_svg}
                  placeholder="enter your first name here"
                  onChange={handleFirstNameChange}
                />
              </InputWithLabel>
              {!firstNameValid && (
                <InputErrorText>Field must not be empty</InputErrorText>
              )}
              <InputWithLabel>
                <RequiredText>Last Name</RequiredText>
                <ErrorInputWithIcon
                  valid={lastNameValid}
                  src={user_svg}
                  placeholder="enter your last name here"
                  onChange={handleLastNameChange}
                />
              </InputWithLabel>
              {!useFullNameAsStageName && (
                <InputWithLabel>
                  <RequiredText>Stage Name</RequiredText>
                  <ErrorInputWithIcon
                    valid={stageNameValid}
                    src={magic_star}
                    placeholder="enter your stage name here"
                    onChange={handleStageNameChange}
                  />
                </InputWithLabel>
              )}
              {!stageNameValid && !useFullNameAsStageName && (
                <InputErrorText>Field must not be empty</InputErrorText>
              )}
              <InputWithLabel>
                <RequiredText>Email</RequiredText>
                <ErrorInputWithIcon
                  valid={emailValid}
                  src={user_svg}
                  placeholder="enter your email here"
                  onChange={handleEmailChange}
                />
              </InputWithLabel>
              {!emailValid && (
                <InputErrorText>
                  Field must not be empty and include an @
                </InputErrorText>
              )}
              <RowWithPadding>
                <label>
                  <Checkbox
                    checked={useFullNameAsStageName}
                    onChanged={onCheckBoxChange}
                  />
                  <span style={{ marginLeft: 8 }}>
                    Use full name as stage name
                  </span>
                </label>
              </RowWithPadding>
            </ArtistInfoForm>
            <SimpleButton onClick={handleSubmit}>Save</SimpleButton>
          </ArtistInfo>
        )}

        {saved && (
          <ArtistInfo>
            <SavedStateContainer>
              <ThankYouText>Thank you!</ThankYouText>
              <Text>Your account is in review for verification.</Text>
              <Text>Once you are verified, you can mint your NFTs</Text>
              <OKButton bg="primary" onClick={onOkClick}>
                OK
              </OKButton>
            </SavedStateContainer>
          </ArtistInfo>
        )}
      </NewArtistInfoContainer>
    </>
  );
};
